/**
 * The mission half of the HUD: what you are supposed to be doing, and which way
 * the next waypoint is.
 *
 * Pure, like `combat-hud.ts` and `hud-snapshot.ts` — strings and numbers a Solid
 * component renders and a unit test asserts, with no GPU and no DOM anywhere
 * near it.
 *
 * The waypoint bearing is *relative to the nose*, like the contact arrow, so
 * both indicators mean the same thing when they point the same way. Distances
 * are in miles: gunnery talks in yards, navigation does not, and the Mission
 * Builder manual's own maps are scaled in miles.
 */

import {
  bearingToRad,
  missionDebrief,
  missionScore,
  currentWaypoint,
  headingAngle,
  horizontalDistanceM,
  objectiveProgressLabel,
  radiansToDegrees,
  type MissionDebrief,
  type MissionOutcome,
  type MissionRun,
  type ObjectiveStatus,
  type Waypoint,
} from "@red-opus/engine";

export const METRES_PER_MILE = 1609.344;

export interface ObjectiveLine {
  readonly id: string;
  readonly label: string;
  /** `"1 / 2"`, or empty for a yes/no objective. */
  readonly progress: string;
  readonly status: ObjectiveStatus;
  readonly primary: boolean;
}

export interface WaypointCue {
  readonly label: string;
  /** `"1.4 mi"`. */
  readonly distance: string;
  /** Relative to the nose in degrees, 0 ahead, +90 right — drives the arrow. */
  readonly bearingDeg: number;
  /** How many legs of the route are left, including this one. */
  readonly remaining: number;
}

export interface MissionHud {
  readonly title: string;
  /** The line the HUD shows big: the first primary objective still outstanding. */
  readonly objective: ObjectiveLine | undefined;
  readonly objectives: readonly ObjectiveLine[];
  readonly waypoint: WaypointCue | undefined;
  readonly outcome: MissionOutcome;
  /**
   * The running MISSION SCORE, as the sortie stands right now.
   *
   * Here because the original's in-flight `ESC` dialog carries a live `SCORE:`
   * readout under its buttons — `docs/game/ui-reference/dialog-mission-not-complete.png`
   * shows it — and that is the one thing on that board the port had no way to
   * fetch. The *readout* is the original's; the *number* is
   * `debrief.ts`'s own invented weighting, which says so at the point of
   * definition and is recorded as INVENTED in `missions.md`.
   *
   * `missionScore` is a pure function of the stats the run already carries, so
   * this costs one call per HUD interval and no new state.
   */
  readonly score: number;
}

/**
 * The mark against each objective in the HUD's objective list (G34/B6: the list
 * was computed and only its first pending primary line was ever drawn).
 *
 * Glyphs rather than words because the column is 22 rem wide and the label has
 * to fit beside them. They are port-side: the original has no in-flight
 * objective list at all — it briefs the mission on the assignment sheet and then
 * tells you how it went by message and at the debriefing.
 */
export const OBJECTIVE_MARKS: Readonly<Record<ObjectiveStatus, string>> = {
  pending: "·",
  complete: "✓",
  failed: "✗",
};

/**
 * The one line the HUD shows once the mission is decided.
 *
 * `THE MISSION IS COMPLETE.` is `RB.EXE`'s own string, verbatim
 * (`docs/game/ui-menus.md` § *In-flight HUD / status messages*). The failure
 * side has no counterpart in the string dump — the original simply does not
 * announce a failed mission in the cockpit — so the port says the plainest
 * thing it can and does not pretend the wording is the game's.
 */
export function missionOutcomeLine(outcome: MissionOutcome): string | undefined {
  if (outcome === "success") return "THE MISSION IS COMPLETE.";
  if (outcome === "failure") return "THE MISSION HAS FAILED.";
  return undefined;
}

export function formatMiles(metres: number): string {
  const miles = metres / METRES_PER_MILE;
  return `${miles < 10 ? miles.toFixed(1) : String(Math.round(miles))} mi`;
}

/** Wraps into (−180, 180], so an arrow never takes the long way round. */
export function relativeBearingDeg(headingRad: number, bearingRad: number): number {
  const degrees = (radiansToDegrees(bearingRad - headingRad) % 360 + 540) % 360 - 180;
  return degrees;
}

export function waypointCue(
  position: { readonly x: number; readonly y: number; readonly z: number },
  headingRad: number,
  waypoint: Waypoint,
  remaining: number,
): WaypointCue {
  return {
    label: waypoint.label,
    distance: formatMiles(horizontalDistanceM(position, waypoint.position)),
    bearingDeg: relativeBearingDeg(headingRad, bearingToRad(position, waypoint.position)),
    remaining,
  };
}

function objectiveLines(run: MissionRun): ObjectiveLine[] {
  return run.spec.objectives.map((objective) => {
    const state = run.objectives.find((entry) => entry.id === objective.id);
    return {
      id: objective.id,
      label: objective.label,
      progress: state ? objectiveProgressLabel(state) : "",
      status: state?.status ?? "pending",
      primary: objective.role === "primary",
    };
  });
}

export interface MissionHudOptions {
  /**
   * REAL NAVIGATION. RE of `RB.EXE` (`docs/engineering/sun-blind-spot-design.md`
   * §3) found the live flag `ds:0x7277` gating a whole block that computes and
   * draws a two-character sector-code readout — exactly the manual's own
   * description ("your sector coordinates will be displayed on the screen"
   * when the toggle is *off*). This port has no sector-grid HUD element to
   * match it against, but it does have a *waypoint* cue the original lacks
   * (a port extension), and hiding that is the same withholding-of-aids the
   * toggle is for: with it on, you fly by map, compass and landmarks, not by
   * a readout. Mission logic is untouched either way — only this field
   * changes; the route still advances and objectives still trigger off the
   * real position, `missionHudFrom` just stops reporting it.
   */
  readonly hideWaypoint?: boolean;
}

export function missionHudFrom(run: MissionRun, options: MissionHudOptions = {}): MissionHud {
  const player = run.encounter.aircraft.find((entry) => entry.id === run.spec.playerFlightId);
  const next = currentWaypoint(player?.route ?? null);
  const lines = objectiveLines(run);
  return {
    title: run.spec.title,
    objective: lines.find((line) => line.primary && line.status === "pending"),
    objectives: lines,
    waypoint:
      !options.hideWaypoint && player && next && player.route
        ? waypointCue(
            player.sim.position,
            headingAngle(player.sim.orientation),
            next,
            player.route.waypoints.length - player.route.index,
          )
        : undefined,
    outcome: run.outcome,
    score: missionScore(run.stats, run.outcome),
  };
}

/**
 * The debriefing card for a mission that is over, or `undefined` while it is
 * still being flown. A one-liner, but it is the whole trigger for the debrief
 * screen appearing, so it is worth having somewhere a test can reach.
 */
export function debriefIfDecided(run: MissionRun): MissionDebrief | undefined {
  return run.outcome === "inProgress" ? undefined : missionDebrief(run);
}

/** The HUD before a scene exists: a title and nothing decided. */
export function idleMissionHud(title: string): MissionHud {
  return { title, objective: undefined, objectives: [], waypoint: undefined, outcome: "inProgress", score: 0 };
}
